/**
 * services/web-ui/src/components/audit/HeatMap.tsx
 *
 * W4-C: SVG heat-map showing stages × last N calls.
 *
 * Row = stage, Col = session (newest on the right).
 * Cell colors:
 *   green  — stage visited, no skip
 *   red    — stage was skipped
 *   gray   — stage not in stages_visited or stages_skipped (didn't apply)
 *
 * v1 note: stages_visited and stages_skipped come from the list endpoint.
 * Yellow "warning" cells are not implemented — there is no warning signal
 * in the current data model.
 */

import type { SessionListItem } from "../../api/voice-sessions-list";

export const DEFAULT_STAGES = [
  "greeting",
  "identification",
  "authentication",
  "intent_capture",
  "data_retrieval",
  "action_execution",
  "confirmation",
  "closing",
];

interface HeatMapProps {
  sessions: SessionListItem[];
  stages?: string[];
  onCellClick?: (sessionId: string, stage: string) => void;
}

type CellState = "visited" | "skipped" | "inactive";

function getCellState(session: SessionListItem, stage: string): CellState {
  const visited = session.stages_visited ?? [];
  const skipped = session.stages_skipped ?? [];
  if (skipped.includes(stage)) return "skipped";
  if (visited.includes(stage)) return "visited";
  return "inactive";
}

const CELL_COLORS: Record<CellState, string> = {
  visited: "#22c55e",   // green-500
  skipped: "#ef4444",   // red-500
  inactive: "#374151",  // gray-700
};

const CELL_LABELS: Record<CellState, string> = {
  visited: "visited",
  skipped: "skipped",
  inactive: "not applicable",
};

const CELL_W = 18;
const CELL_H = 18;
const CELL_GAP = 2;
const LABEL_W = 120;
const LABEL_H = 20;
const PADDING = 8;

export function HeatMap({ sessions, stages = DEFAULT_STAGES, onCellClick }: HeatMapProps) {
  // Newest session on the right — reverse so index 0 = oldest on left
  const ordered = [...sessions].reverse();

  const cols = ordered.length;
  const rows = stages.length;

  const svgW = LABEL_W + PADDING + cols * (CELL_W + CELL_GAP) + PADDING;
  const svgH = LABEL_H + PADDING + rows * (CELL_H + CELL_GAP) + PADDING;

  if (cols === 0) {
    return (
      <div className="flex items-center justify-center h-32 text-gray-600 text-sm">
        No sessions to display
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <svg
        width={svgW}
        height={svgH}
        className="font-mono"
        style={{ minWidth: svgW }}
        aria-label="Compliance heat-map"
      >
        {/* Column header: session indices (oldest → newest) */}
        {ordered.map((_, ci) => (
          <text
            key={ci}
            x={LABEL_W + PADDING + ci * (CELL_W + CELL_GAP) + CELL_W / 2}
            y={LABEL_H - 4}
            textAnchor="middle"
            fontSize={9}
            fill="#6b7280"
          >
            {cols - ci}
          </text>
        ))}

        {stages.map((stage, ri) => {
          const cy = LABEL_H + PADDING + ri * (CELL_H + CELL_GAP);
          return (
            <g key={stage}>
              {/* Row label */}
              <text
                x={LABEL_W - 6}
                y={cy + CELL_H / 2 + 4}
                textAnchor="end"
                fontSize={10}
                fill="#9ca3af"
              >
                {stage}
              </text>

              {/* Cells */}
              {ordered.map((session, ci) => {
                const cx = LABEL_W + PADDING + ci * (CELL_W + CELL_GAP);
                const state = getCellState(session, stage);
                const color = CELL_COLORS[state];
                const sessionId = session.id;

                return (
                  <g key={ci}>
                    <title>
                      {`Session ${cols - ci} · ${stage} · ${CELL_LABELS[state]}\n${session.started_at ? new Date(session.started_at).toLocaleString() : "unknown time"}`}
                    </title>
                    <rect
                      x={cx}
                      y={cy}
                      width={CELL_W}
                      height={CELL_H}
                      rx={2}
                      fill={color}
                      opacity={state === "inactive" ? 0.4 : 0.85}
                      style={{ cursor: onCellClick ? "pointer" : "default" }}
                      onClick={() => onCellClick?.(sessionId, stage)}
                    />
                  </g>
                );
              })}
            </g>
          );
        })}
      </svg>

      {/* Legend */}
      <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
        <LegendItem color={CELL_COLORS.visited} label="Visited" />
        <LegendItem color={CELL_COLORS.skipped} label="Skipped" />
        <LegendItem color={CELL_COLORS.inactive} label="Not applicable" opacity={0.4} />
        <span className="ml-auto text-gray-600">Showing {cols} most recent calls</span>
      </div>
    </div>
  );
}

function LegendItem({
  color,
  label,
  opacity = 0.85,
}: {
  color: string;
  label: string;
  opacity?: number;
}) {
  return (
    <span className="flex items-center gap-1">
      <span
        className="inline-block w-3 h-3 rounded-sm"
        style={{ background: color, opacity }}
      />
      {label}
    </span>
  );
}
